import { memo, SyntheticEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  Dropdown,
  Input,
  Button,
  Icon,
  DropdownItemProps,
  Form,
  FormGroup,
  FormField,
  Image,
} from "semantic-ui-react";

// Commons
import DateTextBox from "../common/DateTextBox";
import PaginationShorthand from "react-lib/appcon/common/PaginationShorthand";

// Components
import BroadcastListTable from "./BroadcastListTable";
import ModMeForm from "./ModMeForm";

// Interfaces
import { ICONS, STYLES } from "./sequence/MeCast";

// Types
import { ActionPostBroadcast, BroadcastAction, BroadcastMode, BroadcastPostParams, MeCastBroadcastListTab, PickedState, RunSequence, STATUS_BROADCAST_APPROVAL_OPTIONS, BROADCAST_TYPE_OPTIONS, ACTIONS_BY_STATUS, ParsedContent, STATUS_BROADCAST_LIST_OPTIONS } from "./MeCastType";
import { DEFAULT_BROADCAST_LIST_LIMIT } from "./MeFormType";

// Serializers
import BroadcastPostSerializer from "@/issara-sdk/types/BroadcastPostSerializer_apps_PRX";

// Hooks
import { useBroadcastPreviews } from "./MeCastHook";

type CardMeCastListProps = {
  // data
  broadcastListTab?: MeCastBroadcastListTab;
  // actions
  onOpenCreateBroadcast: (mode: BroadcastMode, row?: BroadcastPostSerializer) => void;
  onSelectBroadcast: (selected: BroadcastPostSerializer) => void;
  onEditBroadcast?: (selected: BroadcastPostSerializer) => void;
  onDuplicateBroadcast?: (
    selected: BroadcastPostSerializer,
    broadcastParams?: BroadcastPostParams,
    onSuccess?: (duplicatedRow: BroadcastPostSerializer) => void
  ) => void;
  onBroadcastAction: (row: BroadcastPostSerializer, action: ActionPostBroadcast, broadcastParams?: BroadcastPostParams) => void;
  // search
  filter: BroadcastPostParams;
  latestRefresh: string;
  broadcastParams: BroadcastPostParams;
  onChangeData: (e: SyntheticEvent | null, data: any) => void;
  onChangeDate: (name: string, date: string) => void;
  onToggleStatus: (values: BroadcastPostSerializer["status"][]) => void;
  onRefresh: () => void;
  onSearch: () => void;
  // pagination
  activePage: number;
  onChangePage: (page: number) => void;
} & Pick<
  PickedState, "loadingStatus"
>;

type ConfirmActionConfig = {
  header: string;
  description: string;
  question: string;
  confirmAction?: ActionPostBroadcast;
  onConfirm?: (row: BroadcastPostSerializer) => void;
};

const createConfirmActionConfig = (handlers: {
  onDuplicate: (row: BroadcastPostSerializer) => void;
}): Record<BroadcastAction, ConfirmActionConfig> => ({
  CANCEL_REVIEW: {
    header: "Cancel Review Process",
    description: "If you cancel it, the status will change to Drafted.",
    question: "Would you like to cancel review process?",
    confirmAction: "DRAFT",
  },

  CANCEL: {
    header: "Cancel Broadcast",
    description: "If you cancel it, the status will change to Cancelled.",
    question: "Would you like to cancel this broadcast?",
    confirmAction: "CANCEL",
  },

  DUPLICATE: {
    header: "Duplicate Broadcast",
    description: "System will duplicate only content and create new broadcast (Status: Draft)",
    question: "Would you like to duplicate this broadcast?",
    onConfirm: handlers.onDuplicate,
  },

  VIEW: {} as any,
  EDIT: {} as any,
});

// --- Helper Func
const isGroupActive = (
  selected: BroadcastPostSerializer["status"][],
  values: BroadcastPostSerializer["status"][]
) => values.every(v => selected.includes(v));

const CardMeCastList = (props: CardMeCastListProps) => {
  // const [previews, setPreviews] = useState<Record<string, { text: string; media: string | null }>>({});
  const [actionsIndex, setActionsIndex] = useState<number | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    row: BroadcastPostSerializer | null;
    action: BroadcastAction | null;
  }>({ row: null, action: null });

  const broadcastItems = props.broadcastListTab?.broadcastList?.items || [];
  const searchFilter = props.filter;

  const { previews } = useBroadcastPreviews({
    items: broadcastItems,
  });

  const handleSelectAction = (idx: number) => setActionsIndex(actionsIndex === idx ? null : idx);

  const handleCloseAction = () => setActionsIndex(null);

  // const handleOpenConfirm = (row: BroadcastPostSerializer | null, action: BroadcastAction ) => setConfirmAction({ row, action });

  const handleCloseConfirm = () => {
    setConfirmAction({ row: null, action: null });
    
    // --- close action menu if opened
    handleCloseAction();
  };

  const getActionsByRow = (row: BroadcastPostSerializer) => ACTIONS_BY_STATUS[row.status] ?? [];

  const handleDuplicateBroadcast = useCallback(
    (row: BroadcastPostSerializer) => {
      if (!row) return;
      props.onDuplicateBroadcast?.(row, props.broadcastParams, (duplicatedRow) => {
        props.onOpenCreateBroadcast("EDIT", duplicatedRow);
      });
    },
    [props.onDuplicateBroadcast, props.onOpenCreateBroadcast]
  );

  const handleActionClick = useCallback(
    (row: BroadcastPostSerializer, action: BroadcastAction) => {
      if (action === "EDIT") props.onEditBroadcast?.(row);
      if (action === "VIEW") props.onSelectBroadcast(row);

      if (["CANCEL", "CANCEL_REVIEW", "DUPLICATE"].includes(action)) {
        setConfirmAction({ row, action });
      }
    },
    [props.onEditBroadcast, props.onSelectBroadcast]
  );

  const confirmActionConfigMap = useMemo(
    () => createConfirmActionConfig({
      onDuplicate: handleDuplicateBroadcast,
    }), []);

  const confirmConfig = confirmAction.action
    ? confirmActionConfigMap[confirmAction.action]
    : null;

  const handleConfirm = useCallback(() => {
    if (!confirmAction.row || !confirmConfig) return;

    if (confirmConfig.onConfirm) {
      confirmConfig.onConfirm(confirmAction.row);
    }

    if (confirmConfig.confirmAction) {
      props.onBroadcastAction(
        confirmAction.row,
        confirmConfig.confirmAction,
        props.broadcastParams
      );
    }

    handleCloseConfirm();
  }, [confirmAction, confirmConfig, props.onBroadcastAction, props.broadcastParams]);

  const groupOptions: DropdownItemProps[] = useMemo(() => {
    const groups = broadcastItems
      ?.map(row => row.group_name)
      .filter(Boolean) as string[];

    const uniqueGroups = Array.from(new Set(groups));

    return uniqueGroups.map((name) => ({
      key: name,
      text: name,
      value: name,
    }));
  }, [broadcastItems]);

  return (
    <div>
        {/* --- Cancel Broadcast Mod --- */}
        <ModMeForm
          // func
          open={!!confirmConfig}
          onClose={handleCloseConfirm}
          onConfirm={handleConfirm}
          // detail
          header={confirmConfig?.header}
          content={(
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                textAlign: "center",
                lineHeight: 2
              }}
            >
              <label>{confirmConfig?.description}</label>
              <strong>{confirmConfig?.question}</strong>
            </div>
          )}
        />

      <Form>
        {/* --- Filter Section --- */}
        <FormGroup inline>
          <FormField>
            <div style={{ width: "100%" }}>
              {STATUS_BROADCAST_LIST_OPTIONS.map(option => {
                const selectedStatuses = searchFilter.statuses ?? [];
                const active = isGroupActive(selectedStatuses, option.values);

                return (
                  <button
                    key={option.key}
                    onClick={() => props.onToggleStatus(option.values)}
                    style={{
                      border: "1px solid #ccc",
                      backgroundColor: active ? "#f282a0" : "white",
                      color: active ? "white" : "black",
                      padding: "6px 8px",
                      cursor: "pointer",
                      borderRadius: 0,
                      marginRight: "-1px",
                      height: "38.21px",
                    }}
                  >
                    {option.text}
                  </button>
                )
              })}
            </div>
          </FormField>
          <FormField width={2}>
            <div style={{ width: "100%" }}>
              <Input
                name="broadcast_id"
                fluid
                placeholder="Search from ID"
                value={searchFilter.broadcast_id || ""}
                onChange={props.onChangeData}
              />
            </div>
          </FormField>
          {/*
            // ? Dropdown ต้องมีตัวเลือก ALL เพื่อแสดง 2 statuses ด้วยมั้ย
          */}
          <FormField width={2}>
            <div style={{ width: "100%" }}>
              <Dropdown
                name="broadcast_type"
                fluid
                placeholder="Broadcast type"
                selection
                search
                clearable
                options={BROADCAST_TYPE_OPTIONS}
                value={searchFilter.broadcast_type || ""}
                onChange={props.onChangeData}
              />
            </div>
          </FormField>
          <FormField>
            <div style={{ width: "100%" }}>
              <span>Schedule date</span>
            </div>
          </FormField>
          <FormField width={2}>
            <DateTextBox
              value={searchFilter.schedule_broadcasted_start_date || ""}
              onChange={(date) => props.onChangeDate("schedule_broadcasted_start_date", date)}
              placeholder="DD/MM/YYYY"
              inputStyle={{ width: "100%" }}
            />
          </FormField>
          <FormField>
            <div style={{ width: "100%" }}>
              <span>-</span>
            </div>
          </FormField>
          <FormField width={2}>
            <DateTextBox
              value={searchFilter.schedule_broadcasted_end_date || ""}
              onChange={(date) => props.onChangeDate("schedule_broadcasted_end_date", date)}
              placeholder="DD/MM/YYYY"
              inputStyle={{ width: "100%" }}
            />
          </FormField>
          <FormField>
            <div style={{ width: "100%" }}>
              <Dropdown
                name="group_name"
                fluid
                placeholder="Group Name"
                selection
                search
                clearable
                options={groupOptions}
                value={searchFilter.group_name || ""}
                onChange={props.onChangeData}
              />
            </div>
          </FormField>
          <FormField style={{ padding: 0 }}>
            <div style={{ width: "100%" }}>
              <Button
                onClick={props.onSearch}
                style={{ backgroundColor: "#f282a0", color: "white" }}
                loading={props.loadingStatus?.broadcastList}
                icon="search"
              />
            </div>
          </FormField>
        </FormGroup>

        {/* --- Refresh and Create Broadcast Section --- */}
        <FormGroup inline style={{ justifyContent: "space-between" }}>
          <FormField>
            <div style={{ width: "100%" }}>
              {props.loadingStatus?.broadcastList ? (
                <Icon name="spinner" loading />
              ) : (
                <Icon name="redo" style={{ cursor: "pointer" }} onClick={props.onRefresh} />
              )}
              <label style={{ fontStyle: "italic" }}>Last Updated: {props.latestRefresh || "-"}</label>
            </div>
          </FormField>
          <FormField width={3} style={{ padding: 0 }}>
            <div style={{ width: "100%" }}>
              <Button
                fluid
                style={{
                  ...STYLES.primaryThemeBtn,
                  display: "flex",
                  gap: "8px",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={() => props.onOpenCreateBroadcast("CREATE")}
              >
                <Image src={ICONS.broadcast} width="18px" height="18px" />
                <label style={{ cursor: "pointer" }}>Create Broadcast</label>
              </Button>
            </div>
          </FormField>
        </FormGroup>
      </Form>

      {/* --- Table --- */}
      <div style={{ margin: "10px 0px" }}>
        <BroadcastListTable
          // func & callback
          getActions={getActionsByRow}
          onAction={handleActionClick}
          onSelectAction={handleSelectAction}
          // data
          data={props.broadcastListTab?.broadcastList?.items || []}
          previews={previews}
          selectedAction={actionsIndex}
        />
      </div>

      {/* --- Pagination --- */}
      <div style={{ textAlign: "center" }}>
        <PaginationShorthand
          activePage={props.activePage || 1}
          limit={DEFAULT_BROADCAST_LIST_LIMIT}
          total={props.broadcastListTab?.broadcastList?.total}
          onPageChange={props.onChangePage}
        />
      </div>
    </div>
  );
};

CardMeCastList.displayName = "CardMeCastList";

export default memo(CardMeCastList);
